import type { User } from 'firebase/auth';
import { updateProfile } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { NavigateFunction } from 'react-router-dom';
import { upsertProfile } from '../emtRepo';
import { upsertEmployerProfile } from '../employerRepo';
import { db } from '../firebase';
import { signupSourceField } from '../signups/signupSourceCapture';
import type { UserRole } from '../../types/user';

export const SSO_EMPLOYER_PREFILL_KEY = 'ssoEmployerPrefill';

/** Set only when the user explicitly chooses Google → EMT (role-selection or preset). */
export const SSO_EMT_SIGNUP_KEY = 'ssoEmtSignup';

export function markSsoEmtSignup(): void {
  try {
    sessionStorage.setItem(SSO_EMT_SIGNUP_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearSsoEmtSignup(): void {
  try {
    sessionStorage.removeItem(SSO_EMT_SIGNUP_KEY);
  } catch {
    // ignore
  }
}

export function isSsoEmtSignupFlow(): boolean {
  try {
    return sessionStorage.getItem(SSO_EMT_SIGNUP_KEY) === '1';
  } catch {
    return false;
  }
}

export interface SSOEmployerPrefill {
  firstName: string;
  lastName: string;
  email: string;
  photoURL?: string;
}

export function parseDisplayName(displayName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  if (!displayName?.trim()) {
    return { firstName: '', lastName: '' };
  }
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

export async function completeGoogleSignup(
  role: UserRole,
  user: User,
  navigate: NavigateFunction
): Promise<void> {
  const { firstName, lastName } = parseDisplayName(user.displayName);
  const email = user.email ?? '';
  const displayName = user.displayName?.trim() || `${firstName} ${lastName}`.trim();

  if (displayName && !user.displayName) {
    try {
      await updateProfile(user, { displayName });
    } catch {
      // Non-fatal — profile doc will carry the name
    }
  }

  if (role === 'emt') {
    const profileData: Record<string, unknown> = {
      uid: user.uid,
      email,
      firstName,
      lastName,
      displayName,
      onboarding: {
        emt: {
          step: 1,
          completed: false,
        },
      },
      ...signupSourceField(),
    };
    if (user.photoURL) {
      profileData.photoURL = user.photoURL;
    }

    await upsertProfile(user.uid, profileData);
    sessionStorage.setItem('regInProgress', 'emt');
    markSsoEmtSignup();
    navigate('/register/step1', { replace: true });
    return;
  }

  // Employers land straight on the dashboard. Previously this branch wrote NOTHING to
  // Firestore and bounced to /employer-registration, so a Google-authed employer existed in
  // Firebase Auth with no profiles/{uid} doc at all until they finished a 12-field form —
  // that missing doc was the entire source of the friction (and of the "Resolving account…"
  // interstitials elsewhere). We now create the same minimal records employerSignup.ts would,
  // seeded from the Google profile, and let the dismissible profile nudge on the dashboard
  // collect company name / phone / service area / logo afterwards instead of up front.
  const orgId = user.uid; // matches employerSignup.ts's default (org doc id == uid)

  // Fatal if this fails: without profiles/{uid} the employer has no role and can't route.
  // MUST be upsertEmployerProfile (employerRepo), NOT upsertProfile (emtRepo) — the latter
  // hardcodes role:"emt" on every write and would silently mint an EMT profile here.
  await upsertEmployerProfile(user.uid, {
    uid: user.uid,
    email,
    firstName,
    lastName,
    orgId,
    subscriptionPlan: 'free',
    // completed:true mirrors employerSignup.ts — employer "onboarding" is the registration
    // form, which we're deliberately skipping; remaining profile gaps are a soft nudge, not
    // an onboarding gate. Keeps redirectByRole()/AuthContext from bouncing them to
    // /employer/company-setup (which self-bounces to /employer-registration).
    onboarding: { employer: { step: 1, completed: true } },
    ...signupSourceField(),
    createdAt: serverTimestamp(),
    ...(user.photoURL ? { photoURL: user.photoURL } : {}),
  });

  // Non-fatal, same as employerSignup.ts: a missing org doc degrades to an empty company
  // profile the nudge will ask them to fill, not a broken account. Name is intentionally
  // left blank — we have no honest source for it from Google, and a blank name is what makes
  // isOrgProfileComplete() report the profile as incomplete so the nudge surfaces.
  if (db) {
    try {
      await setDoc(
        doc(db, 'organizations', orgId),
        {
          name: '',
          createdBy: user.uid,
          isVerified: false,
          isSeeded: false,
          isClaimed: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (err) {
      console.warn('[completeGoogleSignup] Org doc create failed (non-fatal):', err);
    }
  }

  try {
    localStorage.setItem('role', 'employer');
  } catch {
    // ignore
  }
  // Item 8: go straight to the dashboard on Google sign-in, same as a returning employer
  // (redirectByRole.ts already does this for them). The mandatory backup-password
  // interstitial this used to route through is now a skippable nudge instead —
  // BackupPasswordPrompt, mounted on EmployerDashboardHome, offers the same "set a password
  // so you can still log in if you lose Google access" prompt without blocking the dashboard.
  navigate('/employer/dashboard', { replace: true });
}
